import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  ShoppingBag, Sparkles, Zap, Crown, Store, Smartphone, Shirt, 
  ShoppingCart, Gem, Sofa, Dumbbell, ArrowRight, Star, Eye, Check
} from 'lucide-react';
import api from '../utils/api';

// Template metadata with icons and colors
const TEMPLATE_META = {
  // Metaverse Templates
  metaverse_store: { 
    icon: Sparkles, 
    gradient: 'from-cyan-500 via-purple-500 to-pink-500',
    category: 'Metaverse',
    badge: 'NEW'
  },
  cyberpunk_market: { 
    icon: Zap, 
    gradient: 'from-pink-600 via-purple-600 to-blue-600',
    category: 'Metaverse',
    badge: 'HOT'
  },
  hologram_shop: { 
    icon: Eye, 
    gradient: 'from-emerald-400 via-cyan-500 to-blue-500',
    category: 'Metaverse',
    badge: null
  },
  quantum_commerce: { 
    icon: Star, 
    gradient: 'from-violet-500 via-purple-500 to-pink-500',
    category: 'Metaverse',
    badge: null
  },
  // Flipkart Style
  flipkart_marketplace: { 
    icon: ShoppingBag, 
    gradient: 'from-blue-600 to-blue-800',
    category: 'Marketplace',
    badge: 'FLIPKART'
  },
  amazon_marketplace: { 
    icon: Crown, 
    gradient: 'from-amber-500 to-amber-700',
    category: 'Marketplace',
    badge: 'AMAZON'
  },
  electronics_store: { 
    icon: Smartphone, 
    gradient: 'from-cyan-500 to-blue-600',
    category: 'Electronics',
    badge: null
  },
  fashion_store: { 
    icon: Shirt, 
    gradient: 'from-pink-500 to-rose-600',
    category: 'Fashion',
    badge: 'TRENDING'
  },
  grocery_store: { 
    icon: ShoppingCart, 
    gradient: 'from-green-500 to-emerald-600',
    category: 'Grocery',
    badge: null
  },
  jewelry_store: { 
    icon: Gem, 
    gradient: 'from-yellow-500 to-amber-600',
    category: 'Luxury',
    badge: 'PREMIUM'
  },
  furniture_store: { 
    icon: Sofa, 
    gradient: 'from-amber-700 to-amber-900',
    category: 'Home',
    badge: null
  },
  beauty_store: { 
    icon: Sparkles, 
    gradient: 'from-pink-400 to-purple-500',
    category: 'Beauty',
    badge: null
  },
  sports_store: { 
    icon: Dumbbell, 
    gradient: 'from-orange-500 to-red-600',
    category: 'Sports',
    badge: null
  },
  retail_store: { 
    icon: Store, 
    gradient: 'from-indigo-500 to-purple-600',
    category: 'Retail',
    badge: null
  },
  // Classic
  ecommerce_starter: { 
    icon: ShoppingBag, 
    gradient: 'from-amber-500 to-orange-600',
    category: 'Classic',
    badge: 'FREE'
  },
  portfolio_minimal: { 
    icon: Eye, 
    gradient: 'from-emerald-500 to-teal-600',
    category: 'Portfolio',
    badge: 'FREE'
  },
  business_professional: { 
    icon: Store, 
    gradient: 'from-blue-500 to-indigo-600',
    category: 'Business',
    badge: 'FREE'
  },
  service_provider: { 
    icon: Star, 
    gradient: 'from-pink-500 to-rose-600',
    category: 'Service',
    badge: 'FREE'
  },
  landing_page: { 
    icon: Zap, 
    gradient: 'from-purple-500 to-violet-600',
    category: 'Landing',
    badge: 'FREE'
  },
};

const ShopTemplatesShowcase = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [previewTemplate, setPreviewTemplate] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/shops/templates');
      const templateEntries = Object.entries(res.data.templates);
      setTemplates(templateEntries);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', name: 'All Templates', count: templates.length },
    { id: 'flipkart', name: 'Flipkart-Style', count: templates.filter(([_, t]) => t.is_flipkart_style).length },
    { id: 'classic', name: 'Classic', count: templates.filter(([_, t]) => !t.is_flipkart_style).length },
  ];

  const filteredTemplates = templates.filter(([key, template]) => {
    if (selectedCategory === 'all') return true;
    if (selectedCategory === 'flipkart') return template.is_flipkart_style;
    if (selectedCategory === 'classic') return !template.is_flipkart_style;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="shop-templates-showcase">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-background to-secondary/10" />
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/20 rounded-full blur-[100px]" />
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-secondary/20 rounded-full blur-[120px]" />
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/30 rounded-full text-primary text-sm font-semibold mb-6">
            <Sparkles className="w-4 h-4" />
            {templates.length} Premium Templates
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            <span className="text-white">Flipkart-Style</span>
            <br />
            <span className="bg-gradient-to-r from-primary via-yellow-400 to-primary bg-clip-text text-transparent">
              Shop Templates
            </span>
          </h1>
          
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            Build your dream online store with our premium Flipkart-style templates. 
            Mega menus, flash sales, emergency offers, and more!
          </p>

          <Link 
            to="/login" 
            className="btn-primary inline-flex items-center gap-2"
          >
            Create Your Shop
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-6 border-y border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map(cat => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
                  selectedCategory === cat.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-white/5 hover:bg-white/10'
                }`}
              >
                {cat.name}
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  selectedCategory === cat.id ? 'bg-white/20' : 'bg-white/10'
                }`}>
                  {cat.count}
                </span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Templates Grid */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map(([key, template]) => {
              const meta = TEMPLATE_META[key] || { 
                icon: Store, 
                gradient: 'from-gray-500 to-gray-600',
                category: 'Other',
                badge: null
              };
              const Icon = meta.icon;

              return (
                <div
                  key={key}
                  className="group relative rounded-2xl overflow-hidden border border-white/10 bg-card hover:border-primary/50 transition-all duration-300 hover:-translate-y-2"
                  data-testid={`template-card-${key}`}
                >
                  {/* Preview Header */}
                  <div className={`h-40 bg-gradient-to-br ${meta.gradient} relative`}>
                    {/* Animated background */}
                    <div className="absolute inset-0 opacity-30">
                      <div className="absolute inset-0" style={{
                        backgroundImage: `radial-gradient(circle at 20% 30%, rgba(255,255,255,0.2) 0%, transparent 50%)`
                      }} />
                    </div>
                    
                    {/* Icon */}
                    <div className="absolute top-4 left-4 w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <Icon className="w-7 h-7 text-white" />
                    </div>

                    {/* Badge */}
                    {meta.badge && (
                      <span className={`absolute top-4 right-4 px-3 py-1 rounded-lg text-xs font-bold ${
                        meta.badge === 'FLIPKART' ? 'bg-blue-500 text-white' :
                        meta.badge === 'AMAZON' ? 'bg-amber-500 text-black' :
                        meta.badge === 'NEW' ? 'bg-green-500 text-white' :
                        meta.badge === 'HOT' ? 'bg-red-500 text-white' :
                        meta.badge === 'TRENDING' ? 'bg-pink-500 text-white' :
                        meta.badge === 'PREMIUM' ? 'bg-amber-500 text-black' :
                        meta.badge === 'FREE' ? 'bg-emerald-500 text-white' :
                        'bg-white/20 text-white'
                      }`}>
                        {meta.badge}
                      </span>
                    )}

                    {/* Flipkart Badge */}
                    {template.is_flipkart_style && !meta.badge && (
                      <span className="absolute top-4 right-4 px-3 py-1 bg-blue-600 rounded-lg text-xs font-bold text-white">
                        FLIPKART-STYLE
                      </span>
                    )}

                    {/* Category */}
                    <span className="absolute bottom-4 left-4 px-3 py-1 bg-black/40 backdrop-blur-sm rounded-lg text-xs font-medium text-white">
                      {meta.category}
                    </span>

                    {/* Preview overlay */}
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button
                        onClick={() => setPreviewTemplate({ key, ...template })}
                        className="px-6 py-3 bg-white text-black rounded-xl font-semibold flex items-center gap-2 hover:scale-105 transition-transform"
                      >
                        <Eye className="w-5 h-5" />
                        Preview
                      </button>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-5">
                    <h3 className="font-bold text-lg mb-2">{template.name}</h3>
                    <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                      {template.description}
                    </p>

                    {/* Features */}
                    {template.features && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {template.features.slice(0, 4).map((feature, idx) => (
                          <span 
                            key={idx} 
                            className="px-2 py-1 bg-white/5 rounded-lg text-[10px] font-medium text-muted-foreground capitalize flex items-center gap-1"
                          >
                            <Check className="w-3 h-3 text-green-500" />
                            {feature.replace(/_/g, ' ')}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Sections Count */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {template.sections?.length || 0} Sections
                      </span>
                      <Link 
                        to="/login" 
                        className="text-primary font-medium hover:underline flex items-center gap-1"
                      >
                        Use Template
                        <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Preview Modal */}
      {previewTemplate && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
          <div className="bg-card rounded-3xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className={`h-60 bg-gradient-to-br ${TEMPLATE_META[previewTemplate.key]?.gradient || 'from-gray-500 to-gray-600'} relative rounded-t-3xl`}>
              <button
                onClick={() => setPreviewTemplate(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="absolute bottom-6 left-6">
                <h2 className="text-3xl font-bold text-white mb-2">{previewTemplate.name}</h2>
                <p className="text-white/80">{previewTemplate.description}</p>
              </div>
            </div>

            <div className="p-8">
              {/* Features */}
              <h3 className="text-xl font-bold mb-4">Features</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
                {previewTemplate.features?.map((feature, idx) => (
                  <div key={idx} className="p-3 bg-white/5 rounded-xl flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" />
                    <span className="text-sm capitalize">{feature.replace(/_/g, ' ')}</span>
                  </div>
                ))}
              </div>

              {/* Sections */}
              <h3 className="text-xl font-bold mb-4">Sections Included</h3>
              <div className="flex flex-wrap gap-2 mb-8">
                {previewTemplate.sections?.map((section, idx) => (
                  <span key={idx} className="px-3 py-2 bg-primary/20 text-primary rounded-lg text-sm font-medium capitalize">
                    {section.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>

              {/* Colors */}
              <h3 className="text-xl font-bold mb-4">Color Scheme</h3>
              <div className="flex gap-4 mb-8">
                {Object.entries(previewTemplate.colors || {}).map(([name, color]) => (
                  <div key={name} className="text-center">
                    <div 
                      className="w-12 h-12 rounded-xl mb-2 border border-white/10"
                      style={{ backgroundColor: color }}
                    />
                    <span className="text-xs text-muted-foreground capitalize">{name}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="flex gap-4">
                <Link 
                  to="/login" 
                  className="btn-primary flex-1 justify-center"
                >
                  Create Shop with This Template
                </Link>
                <button
                  onClick={() => setPreviewTemplate(null)}
                  className="px-6 py-3 bg-white/5 rounded-xl font-medium hover:bg-white/10"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-r from-primary/10 via-background to-secondary/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Build Your Shop?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Choose from {templates.filter(([_, t]) => t.is_flipkart_style).length} Flipkart-style templates 
            and launch your online store in minutes.
          </p>
          <Link 
            to="/register" 
            className="btn-primary inline-flex items-center gap-2 text-lg px-8 py-4"
          >
            Get Started Free
            <ArrowRight className="w-6 h-6" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ShopTemplatesShowcase;
